import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type {
  AdminPolicyEndorsementQueueFilters,
  AdministrativeObservedSgaComposition,
  ApiSuccess,
  EndorsementSignedPdfUrl,
  CreateEndorsementOverrideInput,
  MyPolicyEndorsementQueueFilters,
  PlateSuggestionsResponse,
  PolicyEndorsementDetail,
  PolicyEndorsementOverride,
  PolicyEndorsementQueueRow,
  QueueFilterOption,
  ReconcileEndorsementBillingInput,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import { createQueueListingHook, normalizeToPaginated } from './queue/createQueueListingHook';

type QueuePayload = { count: number; rows: PolicyEndorsementQueueRow[] };

export const useEndorsementQueue = createQueueListingHook<
  PolicyEndorsementQueueRow,
  AdminPolicyEndorsementQueueFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.admin(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.adminList(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
      params: filters,
    });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

/** Independent server pages for one administrative Kanban lane. */
export function useEndorsementQueueInfinite(
  filters: Omit<AdminPolicyEndorsementQueueFilters, 'page' | 'limit'>,
  enabled = true,
) {
  const limit = 5;
  return useInfiniteQuery({
    queryKey: [...queryKeys.policyEndorsements.adminList(filters), 'infinite', limit],
    initialPageParam: 1,
    enabled,
    queryFn: async ({ pageParam, signal }) => {
      const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
        params: { ...filters, page: pageParam, limit },
        signal,
      });
      return normalizeToPaginated(response.data.data, { page: pageParam, limit });
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 15_000,
  });
}

export const useMyEndorsementQueue = createQueueListingHook<
  PolicyEndorsementQueueRow,
  MyPolicyEndorsementQueueFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.mine(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.mineList(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<QueuePayload>>('/policy-endorsements/mine', {
      params: filters,
    });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

/**
 * Status terminais do endosso — nenhuma ação externa (pagamento, assinatura,
 * vistoria, SGA) muda mais o resultado. `needs_attention` fica FORA do
 * conjunto que faz polling: assim como `failed`, exige decisão manual de um
 * operador (retry/override), não a chegada de um evento externo.
 */
const ENDORSEMENT_CONVERGENCE_TERMINAL_STATUSES = ['failed', 'cancelled'] as const;

const ENDORSEMENT_CONVERGENCE_POLL_BASE_MS = 8_000;
const ENDORSEMENT_CONVERGENCE_POLL_MAX_MS = 60_000;
/** A cada passo parado deste tamanho, o intervalo dobra até o teto acima. */
const ENDORSEMENT_CONVERGENCE_POLL_BACKOFF_STEP_MS = 60_000;
/** Falhas consecutivas toleradas antes de desistir de um erro que não é 401/403/404. */
const ENDORSEMENT_CONVERGENCE_MAX_POLL_FAILURES = 3;

type EndorsementConvergenceSource = Pick<
  PolicyEndorsementDetail,
  'status' | 'externalReconciliationStatus' | 'signatureGate' | 'updatedAt'
>;

/**
 * TRUE enquanto o endosso aguarda uma ação externa (pagamento, assinatura,
 * vistoria, aplicação/reconciliação SGA) que pode concluir em outra aba,
 * celular ou Magic Link sem que esta tela seja notificada de outra forma.
 */
export function isEndorsementConvergencePending(
  workflow: EndorsementConvergenceSource | undefined,
): workflow is EndorsementConvergenceSource {
  if (!workflow) return false;
  if ((ENDORSEMENT_CONVERGENCE_TERMINAL_STATUSES as readonly string[]).includes(workflow.status)) {
    return false;
  }
  if (workflow.signatureGate.state === 'terminal') return false;
  if (workflow.status === 'needs_attention') return false;
  if (workflow.status === 'applied') {
    // Aplicado localmente mas a reconciliação do SGA ainda depende do
    // fornecedor externo — `divergent` já é erro que exige reconciliação
    // manual (EndorsementOperationsPanel), não convergência automática.
    return workflow.externalReconciliationStatus === 'pending_manual';
  }
  return true;
}

/**
 * Backoff derivado de `updatedAt` (não de estado local em memória): quanto
 * mais tempo o registro passa sem uma transição real no servidor, mais o
 * intervalo cresce, até o teto. Uma transição real sempre atualiza
 * `updatedAt`, então o próximo tick volta sozinho ao intervalo base.
 */
function endorsementConvergencePollIntervalMs(
  workflow: Pick<PolicyEndorsementDetail, 'updatedAt'>,
): number {
  const updatedAtMs = new Date(workflow.updatedAt).getTime();
  const elapsedMs = Number.isNaN(updatedAtMs) ? 0 : Math.max(0, Date.now() - updatedAtMs);
  const steps = Math.floor(elapsedMs / ENDORSEMENT_CONVERGENCE_POLL_BACKOFF_STEP_MS);
  return Math.min(
    ENDORSEMENT_CONVERGENCE_POLL_BASE_MS * 2 ** steps,
    ENDORSEMENT_CONVERGENCE_POLL_MAX_MS,
  );
}

/**
 * 401/403/404 sobrevivendo ao refresh do `apiClient` (ver `client.ts`)
 * significam sessão morta, permissão revogada ou endosso inexistente —
 * repetir a chamada nunca destrava nenhum dos três.
 */
function isIrreversibleEndorsementDetailError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403 || status === 404;
}

/**
 * `refetchOnWindowFocus`/`refetchOnReconnect` compartilham este predicado:
 * a aba que recupera foco ou conexão só vale a pena rebuscar enquanto ainda
 * houver uma ação externa pendente e o erro atual não for definitivo.
 */
function endorsementDetailShouldRefetchOnRecovery(query: {
  state: { data: PolicyEndorsementDetail | undefined; error: unknown };
}): boolean {
  if (isIrreversibleEndorsementDetailError(query.state.error)) return false;
  return isEndorsementConvergencePending(query.state.data);
}

export function useEndorsementDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.detail(id ?? ''),
    queryFn: async (): Promise<PolicyEndorsementDetail> => {
      const response = await apiClient.get<ApiSuccess<PolicyEndorsementDetail>>(
        `/policy-endorsements/${id}`,
      );
      return response.data.data;
    },
    enabled: Boolean(id),
    // Sem `staleTime: 0` o `refetchOnWindowFocus`/`refetchOnReconnect` abaixo
    // seriam enfeite: o TanStack Query só refaz a busca em foco/reconexão
    // quando a query está STALE, e o default global é 5 minutos
    // (query-client.ts) — folga longa demais para "voltei da assinatura no
    // celular e quero ver a tela convergir".
    staleTime: 0,
    // T03 — convergência automática enquanto pagamento/assinatura/vistoria/SGA
    // ficam pendentes em outra aba, celular ou Magic Link. Aba oculta não faz
    // polling (`refetchIntervalInBackground` default false); o foco/reconexão
    // que voltam cobrem a lacuna com um único refetch.
    refetchInterval: (query) => {
      const { data, status, error, errorUpdateCount } = query.state;
      if (isIrreversibleEndorsementDetailError(error)) return false;
      if (status === 'error' && errorUpdateCount >= ENDORSEMENT_CONVERGENCE_MAX_POLL_FAILURES) {
        return false;
      }
      if (!isEndorsementConvergencePending(data)) return false;
      return endorsementConvergencePollIntervalMs(data);
    },
    refetchOnWindowFocus: endorsementDetailShouldRefetchOnRecovery,
    refetchOnReconnect: endorsementDetailShouldRefetchOnRecovery,
  });
}

/**
 * Menor prefixo aceito pelo backend (`PLATE_SUGGESTIONS_MIN_PREFIX_LENGTH`) —
 * evita disparar a query (e o 400 `PLATE_SUGGESTIONS_PREFIX_TOO_SHORT`)
 * enquanto o usuário ainda digita poucos caracteres.
 */
export const PLATE_SUGGESTIONS_MIN_PREFIX_LENGTH = 3;

/**
 * GET /policy-endorsements/plate-suggestions — autocomplete de placa por
 * prefixo (tela /endossos), restrito à carteira do usuário e sem nunca
 * consultar o SGA. `queryFn` recebe `signal` para que o TanStack Query cancele
 * a busca anterior assim que o prefixo debounced mudar. Contrato:
 * docs/contracts/T11-plate-suggestions.md.
 */
export function usePlateSuggestions(params: { prefix: string; limit?: number; enabled?: boolean }) {
  const normalizedPrefix = params.prefix.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const enabled =
    (params.enabled ?? true) && normalizedPrefix.length >= PLATE_SUGGESTIONS_MIN_PREFIX_LENGTH;

  return useQuery({
    queryKey: queryKeys.policyEndorsements.plateSuggestions(normalizedPrefix, params.limit),
    queryFn: async ({ signal }): Promise<PlateSuggestionsResponse> => {
      const response = await apiClient.get<ApiSuccess<PlateSuggestionsResponse>>(
        '/policy-endorsements/plate-suggestions',
        { params: { q: normalizedPrefix, limit: params.limit }, signal },
      );
      return response.data.data;
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export async function fetchEndorsementConsultantOptions(
  search: string,
): Promise<QueueFilterOption[]> {
  const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
    params: { search: search || undefined, page: 1, limit: 100 },
  });
  const unique = new Map<string, string>();
  for (const row of response.data.data.rows) {
    if (row.consultantProfileId && row.consultantName) {
      unique.set(row.consultantProfileId, row.consultantName);
    }
  }
  return [...unique].map(([value, label]) => ({ value, label }));
}

export const usePolicyEndorsement = useEndorsementDetail;

export async function getEndorsementSignedPdf(
  id: string,
  signal?: AbortSignal,
): Promise<EndorsementSignedPdfUrl> {
  const response = await apiClient.get<ApiSuccess<EndorsementSignedPdfUrl>>(
    `/admin/policy-endorsements/${id}/documents/signed-pdf`,
    { signal },
  );
  return response.data.data;
}

/** A fresh, short-lived read capability; never creates a document delivery intent. */
export function useEndorsementSignedPdf(id: string, requestNonce: string | null) {
  return useQuery({
    queryKey: [...queryKeys.policyEndorsements.detail(id), 'signed-pdf', requestNonce],
    queryFn: ({ signal }) => getEndorsementSignedPdf(id, signal),
    enabled: Boolean(id && requestNonce),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export interface RefreshEndorsementSourceIdentitiesInput {
  id: string;
  expectedWorkflowUpdatedAt: string;
  reason: string;
}

/** Explicit source read; loading a detail never refreshes the SGA owner identity. */
export function useRefreshEndorsementSourceIdentities() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    onMutate: async ({ id }: RefreshEndorsementSourceIdentitiesInput) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.policyEndorsements.detail(id),
        exact: true,
      });
    },
    mutationFn: async ({
      id,
      expectedWorkflowUpdatedAt,
      reason,
    }: RefreshEndorsementSourceIdentitiesInput): Promise<PolicyEndorsementDetail> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementDetail>>(
        `/policy-endorsements/${id}/source-identities/refresh`,
        { expectedWorkflowUpdatedAt, reason },
      );
      return response.data.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(queryKeys.policyEndorsements.detail(id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.admin() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.mine() });
    },
  });
}

export function useEndorsementFinancialOrigin(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.financialOrigin(id ?? ''),
    queryFn: async (): Promise<AdministrativeObservedSgaComposition> => {
      const response = await apiClient.get<ApiSuccess<AdministrativeObservedSgaComposition>>(
        `/admin/policy-endorsements/${id}/financial-origin`,
      );
      return response.data.data;
    },
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}

type OverridePayload = { count: number; rows: PolicyEndorsementOverride[] };

export function useEndorsementOverrides(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.overrides(id ?? ''),
    queryFn: async (): Promise<OverridePayload> => {
      const response = await apiClient.get<ApiSuccess<OverridePayload>>(
        `/admin/policy-endorsements/${id}/overrides`,
        { params: { page: 1, limit: 100 } },
      );
      return response.data.data;
    },
    enabled: Boolean(id) && enabled,
  });
}

function useInvalidateEndorsementOperations() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.detail(id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.overrides(id) });
  };
}

export function useCreateEndorsementOverride() {
  const invalidate = useInvalidateEndorsementOperations();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: CreateEndorsementOverrideInput;
    }): Promise<PolicyEndorsementOverride> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementOverride>>(
        `/admin/policy-endorsements/${id}/overrides`,
        input,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
}

export function useRevokeEndorsementOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      overrideId,
    }: {
      endorsementId: string;
      overrideId: string;
    }): Promise<PolicyEndorsementOverride> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementOverride>>(
        `/admin/policy-endorsement-overrides/${overrideId}/revoke`,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.policyEndorsements.overrides(variables.endorsementId),
      });
    },
  });
}

export function useReconcileEndorsementBilling() {
  const invalidate = useInvalidateEndorsementOperations();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: ReconcileEndorsementBillingInput;
    }): Promise<PolicyEndorsementDetail> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementDetail>>(
        `/admin/policy-endorsements/${id}/reconciliation`,
        input,
      );
      return response.data.data;
    },
    onSuccess: (data) => invalidate(data.id),
  });
}
